/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ExclamationCircleIcon,
  ClipboardCopyIcon,
  PaperAirplaneIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { Trans, useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import cronstrue from 'cronstrue';
// English ships inside the core build; the rest register themselves on it.
// Imported one by one on purpose: 'cronstrue/i18n' would pull all ~40
// locales (197 kB minified) into the main chunk, which is under a CI gzip
// budget, to make 7 of them reachable.
import 'cronstrue/locales/es';
import 'cronstrue/locales/fr';
import 'cronstrue/locales/he';
import 'cronstrue/locales/ja';
import 'cronstrue/locales/ru';
import 'cronstrue/locales/zh_CN';
import { callMethod } from '../../../../common/utilsClient/ddp/actions';
import {
  apiZedocGetCustomReportNotificationLog,
  apiZedocGetCustomReportNotificationLogs,
  apiZedocResendCustomReportNotificationLog,
} from '../../../../common/api/zedoc';
import { REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL } from '../../../../common/constants/collections/Projects';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import Table from '../../../../common/components/Table';
import Tag from '../../../../common/components/Tag';
import Button from '../../../../components/Button';
import Dialog from '../../../../components/Dialog';
import Stack from '../../../../common/components/primitives/Stack';
import cleanFilters from '../../../../utils/cleanFilters';
import usePermission from '../../../../utils/usePermission';
import ProjectSelect from '../../../../common/selectors/Project';
import { PROJECT_RESEND_CUSTOM_REPORT } from '../../../../common/permissions';
import store from '../../store';
import DigestLogsConnectedFilters from './DigestLogsConnectedFilters';

const PAGE_SIZE = 100;

/**
 * App language -> cronstrue locale id. Only the languages this app ships
 * differ in spelling; everything else falls back to English.
 */
const CRONSTRUE_LOCALES = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  he: 'he',
  ja: 'ja',
  ru: 'ru',
  zh: 'zh_CN',
};

const toCronstrueLocale = (language) =>
  CRONSTRUE_LOCALES[String(language || '').split('-')[0]] || 'en';

/**
 * "0 9 * * *" -> "At 09:00 AM, every day (Asia/Singapore)". QA asked for
 * plain words here because general users don't read cron; verbose spells out
 * the recurrence instead of leaving a daily schedule looking like a one-off.
 * Translation is cronstrue's own, so it follows the user's language.
 * Unparseable expressions fall back to the raw string so information is
 * never lost.
 *
 * The timezone suffix is not decoration. The worker evaluates this crontab
 * in the PROJECT's timezone (customReportMailUtils getProjectTimezone),
 * while the columns beside this one render real timestamps in the viewer's
 * browser-local time. Without the qualifier a viewer in another timezone
 * reads "09:00" as their own clock and is simply wrong.
 */
const describeCron = (cron, language, timezone) => {
  const raw = String(cron || '').trim();
  if (!raw) {
    return null;
  }
  let text;
  try {
    text = cronstrue.toString(raw, {
      locale: toCronstrueLocale(language),
      verbose: true,
      throwExceptionOnParseError: true,
    });
  } catch (e) {
    text = raw;
  }
  // The raw fallback is evaluated in the project timezone too, so it gets
  // the same qualifier.
  return timezone ? `${text} (${timezone})` : text;
};

const formatWhen = (value, language) => {
  if (!value) {
    return '—';
  }
  // Seconds are kept: the attempt timeline is read to tell two attempts a
  // second apart from each other.
  return (
    toIntlDateTimeFormat(language, new Date(value), {
      dateStyle: 'short',
      timeStyle: 'medium',
    }) || String(value)
  );
};

const formatScheduledLabel = (value, language) => {
  if (!value) {
    return '—';
  }
  return (
    toIntlDateTimeFormat(language, new Date(value), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }) || formatWhen(value, language)
  );
};

const formatDurationMs = (ms) => {
  if (ms == null || !Number.isFinite(Number(ms))) {
    return '—';
  }
  const totalSec = Math.max(0, Math.round(Number(ms) / 1000));
  if (totalSec < 60) {
    return `${totalSec}s`;
  }
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return sec ? `${min}m ${sec}s` : `${min}m`;
};

// Explicit singular/plural keys rather than i18next's count suffixes: the
// seven shipped languages don't share one plural rule, and translators can
// reshape each sentence without the resolver guessing for them.
const recipientCountLabel = (t, count) =>
  count === 1
    ? t('screens:digestLogs.recipients.one')
    : t('screens:digestLogs.recipients.many', { count: count || 0 });

const formatTriggerType = (t, triggerType) => {
  switch (triggerType) {
    case 'scheduled':
      return t('screens:digestLogs.trigger.scheduled');
    case 'auto-retry':
      return t('screens:digestLogs.trigger.autoRetry');
    case 'manual-resend':
      return t('screens:digestLogs.trigger.manualResend');
    default:
      return triggerType || '—';
  }
};

const AttemptTimelineBlocks = ({ attempts }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  if (!Array.isArray(attempts) || !attempts.length) {
    return (
      <p className="text-sm text-gray-500">
        {t('screens:digestLogs.noAttemptEvents')}
      </p>
    );
  }
  return (
    <div className="stack-3">
      {attempts.map((attempt) => {
        const attemptTypeLabel = formatTriggerType(t, attempt.triggerType);
        return (
          <div
            key={attempt.attemptNumber}
            className="rounded border border-divider px-3 py-2"
          >
            <div className="flex flex-wrap items-center gap-2 text-sm font-medium mb-2">
              <span>
                {t('screens:digestLogs.attemptNumber', {
                  number: attempt.attemptNumber,
                })}
              </span>
              <Tag type="default">{attemptTypeLabel}</Tag>
            </div>
            <ul className="text-xs text-gray-500 space-y-1 m-0 pl-4 list-disc">
              {(attempt.events || []).map((event, idx) => (
                <li key={`${attempt.attemptNumber}-${idx}`}>
                  {formatWhen(event.timestamp, language)} — {event.state}
                  {event.recipientEmail ? ` (${event.recipientEmail})` : ''}
                  {event.note ? `: ${event.note}` : ''}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const normalizeResendRecipients = (failedRecipients = []) =>
  (failedRecipients || [])
    .filter((r) => r && r.email)
    .map((r) => ({
      email: r.email,
      userName: r.userName || r.email,
    }));

const statusTagType = (status) => {
  switch (status) {
    case 'SUCCESS':
    case 'sent':
      return 'success';
    case 'RETRYING':
      return 'warning';
    case 'PARTIAL_FAILED':
      return 'warning';
    case 'FAILED':
    case 'failed':
      return 'danger';
    default:
      return 'default';
  }
};

/**
 * EAPP Alerts → Digest logs tab.
 * Lists CustomReportNotificationLogs for alert-digest mail only.
 */
const DigestLogsPanel = ({ projectId, dashboardId, onTotalChange }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const digestFiltersStoreKey = dashboardId
    ? `dashboards.${dashboardId}.digestLogs`
    : 'dashboards.digestLogs';

  const rawFilters = useSelector(store.get(`${digestFiltersStoreKey}.filters`));
  const filters = useMemo(
    () => cleanFilters(rawFilters, { anyOf: true }),
    [rawFilters],
  );

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attemptTimeline, setAttemptTimeline] = useState(null);
  const [attemptTimelineLoading, setAttemptTimelineLoading] = useState(false);
  const [failureNotifyInfo, setFailureNotifyInfo] = useState(null);
  const [failureReasonsInfo, setFailureReasonsInfo] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendConfirm, setResendConfirm] = useState(null);

  const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));
  const canResendCustomReport = usePermission([PROJECT_RESEND_CUSTOM_REPORT], {
    relativeTo: project ? project.getDomains() : [],
  });
  // Same precedence as the worker's getProjectTimezone.
  const projectTimezone = project ? project.getTimezone() : null;

  const loadLogs = useCallback(() => {
    if (!projectId) {
      return;
    }
    setLoading(true);
    dispatch(
      callMethod(apiZedocGetCustomReportNotificationLogs, {
        projectId,
        reportType: REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL,
        filters,
        limit: PAGE_SIZE,
        skip: (page - 1) * PAGE_SIZE,
      }),
    )
      .then((data) => {
        const nextLogs = (data && data.logs) || [];
        const nextTotal = (data && data.total) || nextLogs.length;
        setLogs(nextLogs);
        setTotal(nextTotal);
        if (onTotalChange) {
          onTotalChange(nextTotal);
        }
      })
      .catch(notifyError())
      .finally(() => setLoading(false));
  }, [dispatch, projectId, filters, page, onTotalChange]);

  // Filters changed — the current page may no longer be valid, start over.
  // (loadLogs itself already depends on `filters`, so this only resets the
  // page number; the effect below re-fetches once page settles at 1.)
  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const openDetail = (logId) => {
    setDetailLoading(true);
    dispatch(
      callMethod(apiZedocGetCustomReportNotificationLog, {
        projectId,
        logId,
      }),
    )
      .then((data) => setDetail(data))
      .catch(notifyError())
      .finally(() => setDetailLoading(false));
  };

  const openAttemptTimeline = (logId) => {
    setAttemptTimelineLoading(true);
    setAttemptTimeline(null);
    dispatch(
      callMethod(apiZedocGetCustomReportNotificationLog, {
        projectId,
        logId,
      }),
    )
      .then((data) => setAttemptTimeline(data))
      .catch(notifyError())
      .finally(() => setAttemptTimelineLoading(false));
  };

  const handleResend = (logId, recipientEmails) => {
    setResending(true);
    dispatch(
      callMethod(apiZedocResendCustomReportNotificationLog, {
        projectId,
        logId,
        recipientEmails,
      }),
    )
      .then((result) => {
        notifySuccess(result.message || t('screens:digestLogs.resendQueued'))();
        setResendConfirm(null);
        loadLogs();
        if (detail && detail._id === logId) {
          openDetail(logId);
        }
      })
      .catch(notifyError())
      .finally(() => setResending(false));
  };

  const openResendConfirm = ({ logId, reportName, scheduledTime }) => {
    setResendConfirm({
      logId,
      reportName: reportName || t('screens:digestLogs.alertDigestFallback'),
      scheduledTime,
      recipientCount: 0,
      failedCount: 0,
      resendRecipients: [],
      loading: true,
    });

    dispatch(
      callMethod(apiZedocGetCustomReportNotificationLog, {
        projectId,
        logId,
      }),
    )
      .then((data) => {
        const resendRecipients = normalizeResendRecipients(
          data.failedRecipients,
        );
        setResendConfirm({
          logId,
          reportName:
            data.reportName ||
            reportName ||
            t('screens:digestLogs.alertDigestFallback'),
          scheduledTime: data.scheduledTime || scheduledTime,
          recipientCount: (data.recipients || []).length,
          failedCount: resendRecipients.length,
          resendRecipients,
          loading: false,
        });
      })
      .catch((err) => {
        notifyError()(err);
        setResendConfirm(null);
      });
  };

  const confirmResend = () => {
    if (!resendConfirm) {
      return;
    }
    handleResend(resendConfirm.logId);
  };

  const copyLogId = useCallback(() => {
    if (!detail?._id) {
      return;
    }
    const value = String(detail._id);
    const onCopied = () => notifySuccess(t('screens:digestLogs.logIdCopied'))();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(onCopied).catch(notifyError());
      return;
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      onCopied();
    } catch (err) {
      notifyError()(err);
    }
  }, [detail]);

  const resendTargetCount =
    resendConfirm && (resendConfirm.resendRecipients || []).length;

  const resendRecipientNotice =
    resendConfirm &&
    resendConfirm.failedCount > 0 &&
    resendConfirm.failedCount < resendConfirm.recipientCount
      ? t('screens:digestLogs.resend.noticePartial', {
          recipients: recipientCountLabel(t, resendTargetCount),
        })
      : t('screens:digestLogs.resend.noticeAll', {
          recipients: recipientCountLabel(
            t,
            resendConfirm?.recipientCount || resendTargetCount,
          ),
        });

  const columns = useMemo(
    () => [
      {
        title: t('screens:digestLogs.columns.reportName'),
        dataIndex: 'reportName',
        key: 'reportName',
        render: (name, row) => (
          <button
            type="button"
            className="text-left text-primary-500 hover:underline bg-transparent border-0 p-0 cursor-pointer"
            onClick={() => openDetail(row._id)}
          >
            {name || '—'}
          </button>
        ),
      },
      {
        title: t('screens:digestLogs.columns.schedule'),
        dataIndex: 'cronExpression',
        key: 'cronExpression',
        render: (cron) => {
          const description = describeCron(
            cron,
            i18n.language,
            projectTimezone,
          );
          if (!description) {
            return '—';
          }
          // Verbose cron descriptions get long ("At 0, 15, 30 and 45 minutes
          // past the hour, between 09:00 AM and 05:00 PM, Monday through
          // Friday"), so cap the column and wrap instead of letting one row
          // stretch the whole table.
          return (
            <span
              className="block max-w-56 text-xs whitespace-normal break-words"
              title={cron}
            >
              {description}
            </span>
          );
        },
      },
      {
        title: t('screens:digestLogs.columns.scheduledTime'),
        dataIndex: 'scheduledTime',
        key: 'scheduledTime',
        render: (value) => formatWhen(value, i18n.language),
      },
      {
        title: t('screens:digestLogs.columns.status'),
        dataIndex: 'status',
        key: 'status',
        render: (status, row) => (
          <Tag type={statusTagType(status)}>{row.statusLabel || status}</Tag>
        ),
      },
      {
        title: t('screens:digestLogs.columns.failureReason'),
        dataIndex: 'failureReasons',
        key: 'failureReasons',
        render: (reasons, row) => {
          const list = Array.isArray(reasons) ? reasons.filter(Boolean) : [];
          if (!list.length) {
            return '—';
          }
          const extraCount = list.length - 1;
          return (
            <span className="inline-flex items-start gap-1 text-danger-500">
              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {list[0]}
                {extraCount > 0 ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="text-primary-500 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                      onClick={() =>
                        setFailureReasonsInfo({
                          reportName: row.reportName,
                          reasons: list,
                        })
                      }
                    >
                      {t('screens:digestLogs.moreReasons', {
                        count: extraCount,
                      })}
                    </button>
                  </>
                ) : null}
              </span>
            </span>
          );
        },
      },
      {
        title: t('screens:digestLogs.columns.attempt'),
        key: 'attempt',
        render: (_, row) => {
          const n = row.attemptCount || 1;
          const typeLabel = row.triggerType
            ? formatTriggerType(t, row.triggerType)
            : null;
          return (
            <button
              type="button"
              className="text-left text-primary-500 hover:underline bg-transparent border-0 p-0 cursor-pointer whitespace-nowrap"
              onClick={() => openAttemptTimeline(row._id)}
              title={t('screens:digestLogs.viewAttemptTimeline')}
            >
              #{n}
              {typeLabel ? ` · ${typeLabel}` : ''}
            </button>
          );
        },
      },
      {
        title: t('screens:digestLogs.columns.failureNotifications'),
        key: 'failureNotify',
        render: (_, row) => {
          const status = row.failureNotifyStatus || 'n/a';
          if (status === 'n/a') {
            return (
              <span className="text-gray-500">
                {t('screens:digestLogs.failureNotify.na')}
              </span>
            );
          }
          const label =
            status === 'yes'
              ? t('screens:digestLogs.failureNotify.yes')
              : t('screens:digestLogs.failureNotify.no');
          const emails = (row.failureNotify && row.failureNotify.emails) || [];
          return (
            <button
              type="button"
              className="text-left text-primary-500 hover:underline bg-transparent border-0 p-0 cursor-pointer"
              onClick={() =>
                setFailureNotifyInfo({
                  reportName: row.reportName,
                  status,
                  emails,
                  sentAt: row.failureNotify?.sentAt || null,
                })
              }
              title={t('screens:digestLogs.viewFailureAddresses')}
            >
              {label}
            </button>
          );
        },
      },
      {
        title: t('screens:digestLogs.columns.lastAttempt'),
        dataIndex: 'lastAttemptAt',
        key: 'lastAttemptAt',
        render: (value) => formatWhen(value, i18n.language),
      },
      {
        title: t('screens:digestLogs.columns.nextAttempt'),
        dataIndex: 'nextAttemptAt',
        key: 'nextAttemptAt',
        render: (value, row) => {
          if (row.status === 'RETRYING' && value) {
            return formatWhen(value, i18n.language);
          }
          return value ? formatWhen(value, i18n.language) : '—';
        },
      },
      {
        title: t('screens:digestLogs.columns.destination'),
        dataIndex: 'recipientCount',
        key: 'recipientCount',
        render: (count) => recipientCountLabel(t, count),
      },
      {
        title: t('screens:digestLogs.columns.actions'),
        key: 'actions',
        render: (_, row) => {
          const canResend =
            canResendCustomReport &&
            (row.status === 'FAILED' ||
              row.status === 'PARTIAL_FAILED' ||
              (row.failedCount && row.failedCount > 0));
          if (!canResend) {
            return '—';
          }
          return (
            <Button
              type="ghost"
              size="small"
              disabled={resending}
              icon={<RefreshIcon className="w-4 h-4" />}
              onClick={() =>
                openResendConfirm({
                  logId: row._id,
                  reportName: row.reportName,
                  scheduledTime: row.scheduledTime,
                })
              }
            >
              {t('screens:digestLogs.resend.action')}
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      projectId,
      resending,
      canResendCustomReport,
      projectTimezone,
      t,
      i18n.language,
    ],
  );

  return (
    <Stack>
      <DigestLogsConnectedFilters
        storeKey={digestFiltersStoreKey}
        dashboardId={dashboardId}
        projectId={projectId}
      />
      <div className="flex justify-end">
        <Button size="small" onClick={loadLogs} disabled={loading}>
          {t('screens:digestLogs.refresh')}
        </Button>
      </div>
      {loading && !logs.length ? (
        <p className="text-sm text-gray-500">
          {t('screens:digestLogs.loadingLogs')}
        </p>
      ) : (
        <Table
          rowKey="_id"
          dataSource={logs}
          columns={columns}
          loading={loading}
          pagination={
            total > PAGE_SIZE
              ? {
                  pageSize: PAGE_SIZE,
                  total,
                  current: page,
                  onChange: setPage,
                  showSizeChanger: false,
                }
              : false
          }
        />
      )}

      <Dialog
        visible={!!detail}
        size="xl"
        title={
          detail
            ? t('screens:digestLogs.detail.title', {
                status: detail.statusLabel || detail.status,
              })
            : t('screens:digestLogs.detail.titleFallback')
        }
        onCancel={() => setDetail(null)}
        footer={
          <div className="cluster-2">
            <Button onClick={() => setDetail(null)}>
              {t('screens:digestLogs.close')}
            </Button>
            {canResendCustomReport &&
            detail &&
            detail.failedRecipients &&
            detail.failedRecipients.length ? (
              <Button
                type="primary"
                disabled={resending}
                onClick={() =>
                  openResendConfirm({
                    logId: detail._id,
                    reportName: detail.reportName,
                    scheduledTime: detail.scheduledTime,
                  })
                }
              >
                {resending
                  ? t('screens:digestLogs.queuing')
                  : t('screens:digestLogs.detail.resendFailed')}
              </Button>
            ) : null}
          </div>
        }
      >
        {detailLoading && !detail ? (
          <p className="text-sm text-gray-500">
            {t('screens:digestLogs.loading')}
          </p>
        ) : detail ? (
          <div className="stack-4">
            <div className="text-sm grid grid-cols-2 gap-2">
              <div>
                <strong>{t('screens:digestLogs.detail.digest')}:</strong>{' '}
                {detail.reportName}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.status')}:</strong>{' '}
                <Tag type={statusTagType(detail.status)}>
                  {detail.statusLabel || detail.status}
                </Tag>
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.schedule')}:</strong>{' '}
                {describeCron(
                  detail.cronExpression,
                  i18n.language,
                  projectTimezone,
                ) || '—'}
                {detail.cronExpression ? (
                  <code className="text-xs text-gray-500 ml-2">
                    {detail.cronExpression}
                  </code>
                ) : null}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.scheduled')}:</strong>{' '}
                {formatWhen(detail.scheduledTime, i18n.language)}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.output')}:</strong>{' '}
                {detail.outputType || '—'}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.attempts')}:</strong>{' '}
                {detail.attemptCount}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.trigger')}:</strong>{' '}
                {formatTriggerType(t, detail.triggerType)}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.lastAttempt')}:</strong>{' '}
                {formatWhen(detail.lastAttemptAt, i18n.language)}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.nextAttempt')}:</strong>{' '}
                {detail.nextAttemptAt
                  ? formatWhen(detail.nextAttemptAt, i18n.language)
                  : '—'}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.jobStart')}:</strong>{' '}
                {formatWhen(detail.jobStartTime, i18n.language)}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.jobEnd')}:</strong>{' '}
                {formatWhen(detail.jobEndTime, i18n.language)}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.duration')}:</strong>{' '}
                {formatDurationMs(detail.totalTimeMs)}
              </div>
              <div>
                <strong>{t('screens:digestLogs.detail.counts')}:</strong>{' '}
                {detail.counts
                  ? t('screens:digestLogs.detail.countsValue', {
                      targeted: detail.counts.targeted || 0,
                      sent: detail.counts.sent || 0,
                      failed: detail.counts.failed || 0,
                      skipped: detail.counts.skipped || 0,
                    })
                  : '—'}
              </div>
              <div className="col-span-2">
                <strong>{t('screens:digestLogs.detail.reasons')}:</strong>{' '}
                {(detail.failureReasons || []).join('; ') || '—'}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-2">
                {t('screens:digestLogs.detail.recipients')}
              </h4>
              <Table
                rowKey="email"
                dataSource={detail.recipientOutcomes || []}
                columns={[
                  {
                    title: t(
                      'screens:digestLogs.detail.recipientColumns.email',
                    ),
                    dataIndex: 'email',
                    key: 'email',
                  },
                  {
                    title: t(
                      'screens:digestLogs.detail.recipientColumns.prepare',
                    ),
                    dataIndex: 'prepareStatus',
                    key: 'prepareStatus',
                  },
                  {
                    title: t('screens:digestLogs.detail.recipientColumns.send'),
                    dataIndex: 'sendStatus',
                    key: 'sendStatus',
                  },
                  {
                    title: t(
                      'screens:digestLogs.detail.recipientColumns.reason',
                    ),
                    dataIndex: 'failureReason',
                    key: 'failureReason',
                    render: (v) => v || '—',
                  },
                ]}
                pagination={false}
              />
            </div>
            {Array.isArray(detail.attempts) && detail.attempts.length ? (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  {t('screens:digestLogs.detail.attemptTimeline')}
                </h4>
                <AttemptTimelineBlocks attempts={detail.attempts} />
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-divider text-sm">
              <div className="min-w-0">
                <strong>{t('screens:digestLogs.detail.logId')}:</strong>{' '}
                <code className="text-xs break-all">{detail._id}</code>
              </div>
              <Button
                type="ghost"
                size="small"
                icon={<ClipboardCopyIcon className="w-4 h-4" />}
                onClick={copyLogId}
                aria-label={t('screens:digestLogs.copyLogId')}
              >
                {t('screens:digestLogs.copy')}
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        visible={!!attemptTimeline || attemptTimelineLoading}
        size="large"
        title={
          attemptTimeline
            ? `${t('screens:digestLogs.attemptTimeline.title', {
                number: attemptTimeline.attemptCount || 1,
              })}${
                attemptTimeline.triggerType
                  ? ` · ${formatTriggerType(t, attemptTimeline.triggerType)}`
                  : ''
              }`
            : t('screens:digestLogs.attemptTimeline.titleFallback')
        }
        onCancel={() => {
          setAttemptTimeline(null);
          setAttemptTimelineLoading(false);
        }}
        footer={
          <div className="cluster-2 justify-end">
            <Button
              onClick={() => {
                setAttemptTimeline(null);
                setAttemptTimelineLoading(false);
              }}
            >
              {t('screens:digestLogs.close')}
            </Button>
          </div>
        }
      >
        {attemptTimelineLoading && !attemptTimeline ? (
          <p className="text-sm text-gray-500">
            {t('screens:digestLogs.loading')}
          </p>
        ) : attemptTimeline ? (
          <div className="stack-3">
            <p className="text-sm text-body m-0">
              <strong>{attemptTimeline.reportName}</strong>
              {' · '}
              {formatWhen(attemptTimeline.scheduledTime, i18n.language)}
            </p>
            <AttemptTimelineBlocks attempts={attemptTimeline.attempts} />
          </div>
        ) : null}
      </Dialog>

      <Dialog
        visible={!!failureNotifyInfo}
        size="medium"
        title={
          failureNotifyInfo
            ? t('screens:digestLogs.failureNotify.title', {
                value:
                  failureNotifyInfo.status === 'yes'
                    ? t('screens:digestLogs.failureNotify.yes')
                    : t('screens:digestLogs.failureNotify.no'),
              })
            : t('screens:digestLogs.failureNotify.titleFallback')
        }
        onCancel={() => setFailureNotifyInfo(null)}
        footer={
          <div className="cluster-2 justify-end">
            <Button onClick={() => setFailureNotifyInfo(null)}>
              {t('screens:digestLogs.close')}
            </Button>
          </div>
        }
      >
        {failureNotifyInfo ? (
          <div className="stack-3 text-sm">
            <p className="m-0">
              <strong>
                {failureNotifyInfo.reportName ||
                  t('screens:digestLogs.digestFallback')}
              </strong>
            </p>
            <div>
              <strong>{t('screens:digestLogs.failureNotify.status')}:</strong>{' '}
              {failureNotifyInfo.status === 'yes'
                ? t('screens:digestLogs.failureNotify.sent')
                : t('screens:digestLogs.failureNotify.notSentYet')}
            </div>
            <div>
              <strong>{t('screens:digestLogs.failureNotify.sentAt')}:</strong>{' '}
              {failureNotifyInfo.sentAt
                ? formatWhen(failureNotifyInfo.sentAt, i18n.language)
                : '—'}
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-2">
                {t('screens:digestLogs.failureNotify.addresses')}
              </h4>
              {(failureNotifyInfo.emails || []).length ? (
                <ul className="list-disc pl-5 space-y-1 m-0">
                  {failureNotifyInfo.emails.map((email) => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 m-0">
                  {t('screens:digestLogs.failureNotify.noAddresses')}
                </p>
              )}
            </div>
          </div>
        ) : null}
      </Dialog>

      <Dialog
        visible={!!failureReasonsInfo}
        size="medium"
        title={
          failureReasonsInfo
            ? t('screens:digestLogs.failureReasons.title', {
                name:
                  failureReasonsInfo.reportName ||
                  t('screens:digestLogs.digestFallback'),
              })
            : t('screens:digestLogs.failureReasons.titleFallback')
        }
        onCancel={() => setFailureReasonsInfo(null)}
        footer={
          <div className="cluster-2 justify-end">
            <Button onClick={() => setFailureReasonsInfo(null)}>
              {t('screens:digestLogs.close')}
            </Button>
          </div>
        }
      >
        {failureReasonsInfo ? (
          <ul className="list-disc pl-5 space-y-1 m-0 text-sm">
            {failureReasonsInfo.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : null}
      </Dialog>

      <Dialog
        visible={!!resendConfirm}
        size="medium"
        title={t('screens:digestLogs.resend.title')}
        onCancel={() => !resending && setResendConfirm(null)}
        footer={
          <div className="cluster-2 justify-end">
            <Button disabled={resending} onClick={() => setResendConfirm(null)}>
              {t('screens:digestLogs.cancel')}
            </Button>
            <Button
              type="primary"
              disabled={
                resending || resendConfirm?.loading || !resendTargetCount
              }
              icon={<PaperAirplaneIcon className="w-4 h-4" />}
              onClick={confirmResend}
            >
              {resending
                ? t('screens:digestLogs.queuing')
                : t('screens:digestLogs.resend.confirm')}
            </Button>
          </div>
        }
      >
        {resendConfirm ? (
          <div className="stack-4 text-sm text-body">
            {resendConfirm.loading ? (
              <p className="text-gray-500">
                {t('screens:digestLogs.resend.loadingRecipients')}
              </p>
            ) : (
              <>
                <p>
                  <Trans
                    i18nKey="screens:digestLogs.resend.intro"
                    values={{
                      name: resendConfirm.reportName,
                      when: formatScheduledLabel(
                        resendConfirm.scheduledTime,
                        i18n.language,
                      ),
                    }}
                    components={{ name: <strong /> }}
                  />
                </p>
                <div className="rounded-md border border-warning bg-warning-muted dark:bg-neutral-800 px-4 py-3 text-on-warning dark:text-warning">
                  {t('screens:digestLogs.resend.dataWarning')}
                </div>
                <div className="rounded-md border border-info bg-info-muted px-4 py-3 text-on-info dark:text-info">
                  {resendRecipientNotice}
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    {t('screens:digestLogs.resend.recipientsHeading')}
                  </h4>
                  {resendTargetCount ? (
                    <ul className="list-disc pl-5 space-y-1">
                      {resendConfirm.resendRecipients.map((recipient) => (
                        <li key={recipient.email}>
                          {recipient.userName !== recipient.email
                            ? `${recipient.userName} (${recipient.email})`
                            : recipient.email}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500">
                      {t('screens:digestLogs.resend.noFailedRecipients')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}
      </Dialog>
    </Stack>
  );
};

export default DigestLogsPanel;
